// src/store/feedStore.js
// Community feed: posts, optimistic updates, confirm/flag.
// Seed data initialises the feed on first load.
// User posts persist across sessions; seed posts are re-injected on rehydration.

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { SEED_REPORTS } from '@data/schemas/UserReport';

export const useFeedStore = create(
  persist(
    (set, get) => ({
      // All visible posts (seed + user). Derived on rehydration.
      posts:     SEED_REPORTS,
      // User-authored posts only (persisted subset).
      userPosts: [],

      // Direct posts setter — for simulator/testing only, does not touch userPosts
      setPosts: (posts) => set({ posts }),

      // Optimistically prepend a UserReport-shaped object.
      // Called by writers.js before the sync engine confirms server write.
      addPost: (report) => {
        set((s) => ({
          posts:     [report, ...s.posts],
          userPosts: [report, ...s.userPosts],
        }));
      },

      // Increment confirmCount optimistically (idempotent on server via merge strategy).
      confirmPost: (id) => set((s) => ({
        posts: s.posts.map((p) =>
          p.id === id ? { ...p, confirmCount: (p.confirmCount ?? 0) + 1 } : p
        ),
      })),

      // Increment flagCount optimistically.
      flagPost: (id) => set((s) => ({
        posts: s.posts.map((p) =>
          p.id === id ? { ...p, flagCount: (p.flagCount ?? 0) + 1 } : p
        ),
      })),

      // Hide a post locally (moderation — server write queued separately).
      hidePost: (id) => set((s) => ({
        posts: s.posts.map((p) =>
          p.id === id ? { ...p, isHidden: true } : p
        ),
      })),

      // Mark an SOS as resolved.
      resolveSOSPost: (id) => set((s) => ({
        posts: s.posts.map((p) =>
          p.id === id
            ? { ...p, sosResolved: true, sosResolvedAt: new Date().toISOString() }
            : p
        ),
      })),

      // Replace a post with server-confirmed data after sync reconciliation.
      reconcilePost: (id, serverData) => set((s) => ({
        posts: s.posts.map((p) => p.id === id ? { ...p, ...serverData } : p),
      })),

      // Total count helpers
      removePost: (id) => set(s => ({ posts: s.posts.filter(p => p.id !== id && p.id !== 'report-' + id) })),

      get sosCount()        { return get().posts.filter((p) => p.type === 'sos' && !p.sosResolved).length; },
      get criticalCount()   { return get().posts.filter((p) => (p.severity ?? 0) >= 4).length; },
    }),
    {
      name:    'na-feed',
      storage: createJSONStorage(() => localStorage),

      // Only persist user-authored posts, not seed data.
      partialize: (s) => ({ userPosts: s.userPosts }),

      // On rehydration, rebuild posts = userPosts + seed (deduped by id).
      onRehydrateStorage: () => (state) => {
        if (!state) return;
        const seedIds = new Set(SEED_REPORTS.map((p) => p.id));
        const fresh   = (state.userPosts ?? []).filter((p) => !seedIds.has(p.id));
        state.posts   = [...fresh, ...SEED_REPORTS];
      },
    }
  )
);
